import {
  INlpRequest,
  INlpResponse,
  IPDFDetailsResponse,
  IVideoDetailsResponse,
  IQuestionOption,
  IQueryLog,
  IReference,
  ISaveFeedbackRequest,
  ITopic,
  ITopicTag,
  TopicStatus,
  ReferenceType,
} from '../common/types';

type CuratedQueryRequest = {
  query: string;
  topic_id: string;
  conversation_id: string;
  query_type: INlpRequest['queryType'];
  instance_id: string;
};

type FeedbackRequest = {
  query_id: string;
  question_id: string;
  value: number;
};

type V2Reference = {
  reference_type?: string;
  sourceDocument_id?: string;
  source_document_id?: string;
  header_id?: string | null;
  header_title?: string | null;
  external_document_link?: string | null;
  name?: string | null;
};

type V2QuestionOption = {
  question_id: string;
  actual_query: string;
  option_type: 'CERTAIN_OPTION' | 'UNCERTAIN_OPTION';
  answer: string | null;
  status: string;
  paraphrases?: string[];
  references?: V2Reference[];
  similarity: number;
};

type V2CuratedQueryResponse = {
  query?: {
    id: string;
    query: string;
  };
  question_options?: V2QuestionOption[];
};

type V2QueryLogResponse = {
  id: string;
  query: string;
  status?: string | null;
  created_at?: string;
  updated_at?: string;
};

type V2TopicTag = ITopicTag;

type V2Topic = {
  id: string;
  name: string;
  display_name?: string | null;
  displayName?: string | null;
  legal_name?: string | null;
  legalName?: string | null;
  status?: TopicStatus;
  group_id?: string | null;
  groupId?: string | null;
  group_name?: string | null;
  groupName?: string | null;
  group_synonyms?: string[] | null;
  groupSynonyms?: string[] | null;
  keywords?: string[];
  tags?: V2TopicTag[] | null;
};

export function createCuratedQueryRequest(query: INlpRequest): CuratedQueryRequest {
  return {
    query: query.query,
    topic_id: query.topicId,
    conversation_id: query.conversationId,
    query_type: query.queryType,
    instance_id: query.instanceId,
  };
}

export function createFeedbackRequest({
  queryId,
  questionId,
  value,
}: ISaveFeedbackRequest): FeedbackRequest {
  return {
    query_id: queryId,
    question_id: questionId,
    value: Number(value),
  };
}

export function normalizeCuratedQueryResponse(data: V2CuratedQueryResponse): INlpResponse {
  return {
    query: data.query,
    questionOptions: data.question_options?.map(normalizeQuestionOption) ?? [],
  };
}

export function normalizeQueryLogResponse(data: V2QueryLogResponse): IQueryLog {
  return {
    id: data.id,
    query: data.query,
    status: data.status,
    createdAt: data.created_at,
    updatedAt: data.updated_at,
  };
}

export function normalizeTopicsResponse(data: V2Topic[]): ITopic[] {
  return data.map((topic) => {
    const displayName = topic.display_name ?? topic.displayName ?? topic.name;
    const legalName = topic.legal_name ?? topic.legalName ?? displayName;
    const tags = topic.tags ?? [];

    // Resolve once before storing topics so every consumer uses the same label.
    const useLegalName = legalName && tags.some((tag) => tag.name === displayName);

    return {
      id: topic.id,
      name: topic.name,
      displayName: useLegalName ? legalName : displayName,
      legalName,
      status: normalizeTopicStatus(topic.status),
      groupId: topic.group_id ?? topic.groupId ?? null,
      groupName: topic.group_name ?? topic.groupName ?? null,
      groupSynonyms: topic.group_synonyms ?? topic.groupSynonyms ?? [],
      tags,
      keywords: topic.keywords ?? [],
      docs: [],
    };
  });
}

export function normalizePDFDetailsResponse(data: IPDFDetailsResponse): IPDFDetailsResponse {
  return data;
}

export function normalizeVideoDetailsResponse(data: IVideoDetailsResponse): IVideoDetailsResponse {
  return data;
}

function normalizeQuestionOption(questionOption: V2QuestionOption): IQuestionOption {
  return {
    questionId: questionOption.question_id,
    actualQuery: questionOption.actual_query,
    optionType: questionOption.option_type,
    answer: questionOption.answer ?? '',
    status: questionOption.status,
    paraphrases: questionOption.paraphrases ?? [],
    sourceDocuments: [],
    similarity: questionOption.similarity,
    references: questionOption.references?.map(normalizeReference) ?? [],
  };
}

function normalizeReference(reference: V2Reference): IReference {
  return {
    referenceType: normalizeReferenceType(reference.reference_type),
    sourceDocumentId: reference.sourceDocument_id ?? reference.source_document_id ?? '',
    headerId: reference.header_id ?? '',
    headerTitle: reference.header_title ?? null,
    externalDocumentLink: reference.external_document_link ?? '',
    name: reference.name ?? '',
  };
}

function normalizeReferenceType(referenceType: string | undefined): ReferenceType {
  if (Object.values(ReferenceType).includes(referenceType as ReferenceType)) {
    return referenceType as ReferenceType;
  }

  return ReferenceType.Web;
}

function normalizeTopicStatus(status: TopicStatus | undefined): TopicStatus {
  if (status && Object.values(TopicStatus).includes(status)) {
    return status;
  }

  return TopicStatus.Active;
}
